/* eslint-disable jira/import-whitelist */
import { fg } from '@atlassian/jira-feature-gating';
import { createResource, type RouteResource } from '@atlassian/react-resource-router';
import type { RouteGroup } from '@jira/platform__routing-route-name-types/src/RouteGroup.tsx';
import { preloadPostOfficePlacementWithTenantContext } from '@jira/post-office__post-office-provider-preload-placement/src/preload-placement.tsx';
import { resourceWithCondition } from '@jira/router-resources__utils/src/resourceWithCondition.tsx';

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const postOfficeScreenSpaceFlagsResource: RouteResource<any> = createResource<any>({
	type: 'POST_OFFICE_PLACEMENT__SCREEN_SPACE_FLAGS',
	getKey: () => 'state',
	getData: async (_, { tenantContext }) => {
		const { isAnonymous } = tenantContext;
		if (isAnonymous) {
			return null;
		}

		return preloadPostOfficePlacementWithTenantContext('screen-space-flags', tenantContext);
	},
	maxAge: Infinity,
});

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const postOfficeJiraSideNavResource: RouteResource<any> = createResource<any>({
	type: 'POST_OFFICE_PLACEMENT__JIRA_SIDE_NAV',
	getKey: () => 'state',
	getData: async (_, { tenantContext }) => {
		const { isAnonymous } = tenantContext;
		if (isAnonymous) {
			return null;
		}

		return preloadPostOfficePlacementWithTenantContext('jira-side-nav', tenantContext);
	},
	maxAge: Infinity,
	isCritical: true,
});

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const postOfficeJiraYourWorkResource: RouteResource<any> = createResource<any>({
	type: 'POST_OFFICE_PLACEMENT__JIRA_YOUR_WORK',
	getKey: () => 'state',
	getData: async (_, { tenantContext }) => {
		const { isAnonymous } = tenantContext;
		if (isAnonymous) {
			return null;
		}

		return preloadPostOfficePlacementWithTenantContext('jira-your-work', tenantContext);
	},
	maxAge: Infinity,
});

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const postOfficeJiraTopBannerResource: RouteResource<any> = createResource<any>({
	type: 'POST_OFFICE_PLACEMENT__JIRA_TOP_BANNER',
	getKey: () => 'state',
	getData: async (_, { tenantContext }) => {
		const { isSiteAdmin } = tenantContext;
		if (isSiteAdmin) {
			return preloadPostOfficePlacementWithTenantContext('jira-top-banner', tenantContext);
		}

		return null;
	},
	maxAge: Infinity,
});

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const conditionalPostOfficeJiraTopBannerResource: RouteResource<any> = resourceWithCondition(
	() => fg('jira-issue-term-refresh-banner'),
	postOfficeJiraTopBannerResource,
);

// oxlint-disable-next-line @typescript-eslint/no-explicit-any
export const postOfficeNav4SectionResource: RouteResource<any> = createResource<any>({
	type: 'POST_OFFICE_PLACEMENT__NAV_4_SECTION',
	getKey: () => 'state',
	getData: async (_, { tenantContext }) => {
		const { isAnonymous } = tenantContext;
		if (isAnonymous) {
			return null;
		}

		return preloadPostOfficePlacementWithTenantContext('nav-4-section', tenantContext);
	},
	maxAge: Infinity,
});

const nav4ProjectsSectionEnabledGroup = new Set<RouteGroup>([
	'classic-business',
	'classic-software',
	'home',
	'issue',
	'issue-navigator',
	'legacy',
	'polaris',
	'servicedesk',
	'software',
	'software-reports-boardless',
]);

export const postOfficeNav4ProjectsSectionResource: RouteResource<Response | null | undefined> =
	createResource({
		type: 'POST_OFFICE_PLACEMENT__NAV_4_PROJECTS_SECTION',
		getKey: () => 'state',
		getData: async ({ route: { group } }, { tenantContext }) => {
			const { isAnonymous } = tenantContext;
			if (isAnonymous) {
				return null;
			}

			if (!group || !nav4ProjectsSectionEnabledGroup.has(group)) {
				return null;
			}

			return preloadPostOfficePlacementWithTenantContext('nav-4-projects-section', tenantContext);
		},
		maxAge: Infinity,
	});

const FREE_EDITION = 'FREE_EDITION' as const;

export const postOfficeAssetsMenuItemFreeUsersPlacementResource: RouteResource<
	Response | null | undefined
> = createResource({
	type: 'POST_OFFICE_PLACEMENT__ASSETS_MENU_ITEM_FREE_USERS_PLACEMENT',
	getKey: () => 'state',
	getData: async (_, { tenantContext }) => {
		const { appEditions, isSiteAdmin } = tenantContext;
		if (!isSiteAdmin || appEditions?.serviceDesk !== FREE_EDITION) {
			return null;
		}

		return preloadPostOfficePlacementWithTenantContext(
			'jira-navbar-assets-app-section',
			tenantContext,
		);
	},
	maxAge: Infinity,
});

const STANDARD_EDITION = 'STANDARD_EDITION' as const;

export const postOfficeAssetsSideNavUpsellDashboardsPlacementResource: RouteResource<
	Response | null | undefined
> = createResource({
	type: 'POST_OFFICE_PLACEMENT__ASSETS_SIDE_NAV_UPSELL_DASHBOARDS_PLACEMENT',
	getKey: () => 'state',
	getData: async (_, { tenantContext }) => {
		const { isSiteAdmin, appEditions } = tenantContext;
		if (!isSiteAdmin || appEditions?.serviceDesk !== STANDARD_EDITION) {
			return null;
		}

		return preloadPostOfficePlacementWithTenantContext(
			'jsm-assets-app-navbar-dashboards-section',
			tenantContext,
		);
	},
	maxAge: Infinity,
});

export const postOfficeAssetsSideNavUpsellDataManagerPlacementResource: RouteResource<
	Response | null | undefined
> = createResource({
	type: 'POST_OFFICE_PLACEMENT__ASSETS_SIDE_NAV_UPSELL_DATA_MANAGER_PLACEMENT',
	getKey: () => 'state',
	getData: async (_, { tenantContext }) => {
		const { isSiteAdmin, appEditions } = tenantContext;
		if (!isSiteAdmin || appEditions?.serviceDesk !== 'STANDARD_EDITION') {
			return null;
		}

		return preloadPostOfficePlacementWithTenantContext(
			'jsm-assets-app-navbar-data-manager-section',
			tenantContext,
		);
	},
	maxAge: Infinity,
});
